import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { reviewAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui';
import { capitalize } from '@/lib/utils';
import { Send, ArrowUpDown } from 'lucide-react';

interface Email {
  id: string;
  from_address: string;
  subject: string;
  category: string;
  received_at: string;
  response_status?: string;
  extracted_entities?: { order_id?: string };
  platform?: string;
}

export default function ApprovedQueue() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [categoryFilter, setCategoryFilter] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [orderIdSearch, setOrderIdSearch] = useState('');
  const [debouncedOrderId, setDebouncedOrderId] = useState('');
  const [dateRange, setDateRange] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedLimit, setSelectedLimit] = useState<string>('all');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    
    const category = params.get('category');
    const platform = params.get('platform');
    const orderSearch = params.get('orderSearch');
    const startDate = params.get('startDate');
    const endDate = params.get('endDate');
    const sort = params.get('sort');
    const page = params.get('page');
    
    if (category) setCategoryFilter(category);
    if (platform) setPlatformFilter(platform);
    if (orderSearch) setOrderIdSearch(orderSearch);
    if (startDate) setStartDate(startDate);
    if (endDate) setEndDate(endDate);
    if (sort && (sort === 'newest' || sort === 'oldest')) setSortOrder(sort);
    if (page) setCurrentPage(parseInt(page) || 1);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    
    if (categoryFilter && categoryFilter !== 'all') params.set('category', categoryFilter);
    if (platformFilter && platformFilter !== 'all') params.set('platform', platformFilter);
    if (orderIdSearch) params.set('orderSearch', orderIdSearch);
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (sortOrder !== 'newest') params.set('sort', sortOrder);
    if (currentPage !== 1) params.set('page', currentPage.toString());
    
    const newUrl = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;
    window.history.replaceState({}, '', newUrl);
  }, [categoryFilter, platformFilter, orderIdSearch, startDate, endDate, sortOrder, currentPage]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedOrderId(orderIdSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [orderIdSearch]);

  const { data: approvedResponse, isLoading } = useQuery({
    queryKey: ['approved-emails'],
    queryFn: async () => {
      const response = await reviewAPI.getApproved();
      return response.data;
    },
  });

  const emails = approvedResponse?.data || [];

  const filteredEmails = emails?.filter((email: Email) => {
    if (categoryFilter && categoryFilter !== 'all' && email.category !== categoryFilter) return false;
    if (platformFilter && platformFilter !== 'all' && (email.platform || 'amazon') !== platformFilter) return false;
    if (debouncedOrderId && !email.extracted_entities?.order_id?.includes(debouncedOrderId)) return false;
    
    if (dateRange !== 'all') {
      const emailDate = new Date(email.received_at);
      const now = new Date();
      
      if (dateRange === 'today') {
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        if (emailDate < today) return false;
      } else if (dateRange === 'week') {
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        if (emailDate < weekAgo) return false;
      } else if (dateRange === 'month') {
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        if (emailDate < monthAgo) return false;
      } else if (dateRange === 'custom' && startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        if (emailDate < start || emailDate > end) return false;
      }
    }
    
    return true;
  });

  const sendMutation = useMutation({
    mutationFn: (emailIds: string[]) => 
      Promise.all(emailIds.map(id => reviewAPI.send(id))),
    onSuccess: () => {
      toast.success('Emails sent successfully');
      setSelectedEmails(new Set());
      queryClient.invalidateQueries({ queryKey: ['approved-emails'] });
    },
  });

  const toggleEmail = (id: string) => {
    const newSelected = new Set(selectedEmails);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedEmails(newSelected);
  };

  const handleSendSelected = () => {
    if (selectedEmails.size === 0) return;
    if (window.confirm(`Send ${selectedEmails.size} email(s)?`)) {
      sendMutation.mutate(Array.from(selectedEmails));
    }
  };

  const handleSendSingle = (id: string) => {
    if (window.confirm('Send this email?')) {
      sendMutation.mutate([id]);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const limitedEmails = selectedLimit === 'all' 
    ? filteredEmails 
    : filteredEmails?.slice(0, parseInt(selectedLimit));

  const sortedEmails = sortOrder === 'oldest' 
    ? [...(limitedEmails || [])].reverse() 
    : limitedEmails;

  const totalPages = Math.ceil((sortedEmails?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedEmails = sortedEmails?.slice(startIndex, endIndex);

  return (
    <div className="h-full bg-white flex flex-col">
      <div className="border-b border-gray-200 px-8 py-4">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-semibold text-gray-900">Send Emails</h1>
          {selectedEmails.size > 0 && (
            <Button 
              onClick={handleSendSelected} 
              disabled={sendMutation.isPending}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Send className="h-4 w-4 mr-2" />
              Send Selected ({selectedEmails.size})
            </Button>
          )}
        </div>
        
        <div className="relative">
          <Input
            type="text"
            placeholder="Search by order ID..."
            value={orderIdSearch}
            onChange={(e) => setOrderIdSearch(e.target.value)}
            className="w-full"
          />
        </div>
      </div>

      <div className="border-b border-gray-200 px-8 py-3 bg-gray-50">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Approved ({filteredEmails?.length || 0})
          </h2>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSortOrder(sortOrder === 'newest' ? 'oldest' : 'newest')}
            >
              <ArrowUpDown className="h-4 w-4 mr-1" />
              {sortOrder === 'newest' ? 'Newest First' : 'Oldest First'}
            </Button>
          </div>
        </div>
      </div>

      <div className="border-b border-gray-200 px-8 py-3 flex items-center gap-2 overflow-x-auto">
        <Select value={categoryFilter || 'all'} onValueChange={setCategoryFilter}>
          <SelectTrigger className="h-8 w-[160px]">
            <SelectValue placeholder="Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value="customer_question">Customer Question</SelectItem>
            <SelectItem value="product_question">Product Question</SelectItem>
            <SelectItem value="shipping_question">Shipping Question</SelectItem>
            <SelectItem value="damaged_defective">Damaged Defective</SelectItem>
            <SelectItem value="wrong_item">Wrong Item</SelectItem>
            <SelectItem value="order_cancellation">Order Cancellation</SelectItem>
            <SelectItem value="return_policy">Return Policy</SelectItem>
            <SelectItem value="battery_support">Battery Support</SelectItem>
          </SelectContent>
        </Select>

        <Select value={platformFilter} onValueChange={setPlatformFilter}>
          <SelectTrigger className="h-8 w-[140px]">
            <SelectValue placeholder="Platform" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Platforms</SelectItem>
            <SelectItem value="amazon">Amazon</SelectItem>
            <SelectItem value="ebay">eBay</SelectItem>
          </SelectContent>
        </Select>

        <Select value={dateRange} onValueChange={(value) => {
          setDateRange(value);
          if (value !== 'custom') {
            setStartDate('');
            setEndDate('');
          }
        }}>
          <SelectTrigger className="h-8 w-[160px]">
            <SelectValue placeholder="Timeframe" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Time</SelectItem>
            <SelectItem value="today">Today</SelectItem>
            <SelectItem value="week">Last 7 Days</SelectItem>
            <SelectItem value="month">Last 30 Days</SelectItem>
            <SelectItem value="custom">Custom</SelectItem>
          </SelectContent>
        </Select>

        <Select value={selectedLimit} onValueChange={setSelectedLimit}>
          <SelectTrigger className="h-8 w-[140px]">
            <SelectValue placeholder="Show All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Show All</SelectItem>
            <SelectItem value="25">Last 25</SelectItem>
            <SelectItem value="50">Last 50</SelectItem>
            <SelectItem value="100">Last 100</SelectItem>
          </SelectContent>
        </Select>

        {dateRange === 'custom' && (
          <>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="h-8 w-auto"
            />
            <span className="text-sm text-gray-500">to</span>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="h-8 w-auto"
            />
          </>
        )}
      </div>

      <div className="flex-1 overflow-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
            <tr>
              <th className="w-10 px-4 py-3">
                {paginatedEmails && paginatedEmails.length > 0 && (
                  <Checkbox
                    checked={paginatedEmails.every((email: Email) => selectedEmails.has(email.id))}
                    onCheckedChange={(checked: boolean) => {
                      if (checked) {
                        const newSelected = new Set(selectedEmails);
                        paginatedEmails.forEach((email: Email) => newSelected.add(email.id));
                        setSelectedEmails(newSelected);
                      } else {
                        const newSelected = new Set(selectedEmails);
                        paginatedEmails.forEach((email: Email) => newSelected.delete(email.id));
                        setSelectedEmails(newSelected);
                      }
                    }}
                  />
                )}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Platform</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : paginatedEmails?.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                  No approved emails ready to send
                </td>
              </tr>
            ) : (
              paginatedEmails?.map((email: Email) => (
                <tr
                  key={email.id}
                  onClick={() => navigate(`/review/${email.id}`)}
                  className="hover:bg-gray-50 cursor-pointer"
                >
                  <td className="px-4 py-3">
                    <Checkbox
                      checked={selectedEmails.has(email.id)}
                      onCheckedChange={() => toggleEmail(email.id)}
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {new Date(email.received_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {capitalize(email.category?.replace(/_/g, ' ') || 'Query')}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <Badge className={
                      (email.platform || 'amazon') === 'amazon' 
                        ? 'bg-orange-100 text-orange-800 hover:bg-orange-100' 
                        : 'bg-blue-100 text-blue-800 hover:bg-blue-100'
                    }>
                      {(email.platform || 'amazon').toUpperCase()}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {email.from_address?.split('<')[0].trim() || email.from_address?.split('@')[0] || 'Unknown'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 max-w-md truncate">
                    {email.subject}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                      Approved
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {email.extracted_entities?.order_id || '-'}
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-blue-200 text-blue-700 hover:bg-blue-50"
                      onClick={() => handleSendSingle(email.id)}
                      disabled={sendMutation.isPending}
                    >
                      <Send className="h-3 w-3 mr-1" />
                      Send
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="border-t border-gray-200 px-8 py-3 flex items-center justify-between bg-gray-50">
        <div className="text-sm text-gray-600">
          Showing {startIndex + 1} - {Math.min(endIndex, sortedEmails?.length || 0)} of {sortedEmails?.length || 0}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          
          {totalPages > 1 && Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const pageNum = i + 1;
            return (
              <Button
                key={pageNum}
                variant={currentPage === pageNum ? "default" : "outline"}
                size="sm"
                onClick={() => setCurrentPage(pageNum)}
                className={currentPage === pageNum ? "bg-blue-600 text-white" : ""}
              >
                {pageNum}
              </Button>
            );
          })}
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
